<template>
	<v-menu
		ref="metadataPreview"
		v-model="dialog"
		content-class="metadata-preview"
		:open-on-hover="true"
		:persistent="true"
		:close-on-content-click="false"
		:min-width="minWidth"
		:min-height="minHeight"
		transition="false"
	>
		<template #activator="{ props }">
			<div>
				<slot v-bind="props" name="activator"></slot>
			</div>
		</template>
		<div class="menu-container">
			<div class="triangle" :style="triangleStyle"></div>
			<v-metadata-preview-card
				v-for="(displayDatum, displayDatumIndex) in displayData"
				:key="displayDatumIndex"
				:display-datum="displayDatum"
				:header-row="headerRow"
			/>
		</div>
	</v-menu>
</template>

<script>
import { useTheme } from "vuetify";
import createCompositeKeyUtils from "@/compositeKeyUtils";
import VMetadataPreviewCard from "@/components/VMetadataPreview/VMetadataPreviewCard.vue";

export default {
	name: "VMetadataPreviewV2",

	components: {
		VMetadataPreviewCard,
	},

	props: {
		bounds: { type: Object, default: null },
	},

	setup() {
		const theme = useTheme();
		return { theme };
	},

	data() {
		return {
			compositeKeyData: [],
			data: {},
			dialog: false,
			displayData: [],
			headerRow: {
				axisLabel: "Axis",
				tagNameLabel: "Tag",
				valueLabel: "Value",
			},
			position: { x: 0, y: 0 },
			offset: 12,
			minWidth: 300,
			minHeight: 100,
			width: 300,
			height: 100,
			rowData: [],
			tagGroupName: "",
			triangleStyle: {
				position: "absolute",
				width: "0",
				height: "0",
				border: "8px solid transparent",
			},
		};
	},

	methods: {
		async open(args, tags) {
			this.compositeKeyUtils = createCompositeKeyUtils(tags);
			this.position = args.position;
			this.data = args.data;

			if (!args.data) {
				return;
			}

			this.displayData = [];

			for (const datum of this.data) {
				this.displayData.push({
					rowData: Object.keys(datum)
						.filter((key) => !isNaN(key) && !datum[key].hidden)
						.map((key) => {
							return {
								tagName: datum[key].tag.name,
								value: datum[key].value,
								axis: datum[key].axis,
								fromBroadcast: datum[key].fromBroadcast,
								datasetUuid: datum[key].datasetUuid,
							};
						}),
					tagGroupName: datum.tagGroup.name,
					compositeKeyData: this.compositeKeyUtils.parseCompositeKeyWithLabels(
						datum.compositeKey,
					),
				});
			}

			this.dialog = true;

			await this.$nextTick();
			const element = document.getElementsByClassName("metadata-preview")[0];

			const arrowColor = this.theme.current.value.colors.secondary;
			if (element) {
				element.style.pointerEvents = "none";
				const y = this.position.y + this.bounds.top;
				const x = this.position.x + this.bounds.left;

				const viewportBounds = element.parentElement.getBoundingClientRect();
				const viewportMaxY = viewportBounds.height;
				const viewportMaxX = viewportBounds.width;
				const menuWidth = element.getBoundingClientRect().width;
				const menuHeight = element.getBoundingClientRect().height;
				this.height = menuHeight;
				this.width = menuWidth;

				const direction = this.determineDirection(x, y);

				// Update triangle style based on direction
				switch (direction) {
					case "north":
						this.triangleStyle = {
							...this.triangleStyle,
							bottom: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderTopColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderBottom: "none",
						};
						element.style.bottom = `${viewportMaxY - y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "east":
						this.triangleStyle = {
							...this.triangleStyle,
							left: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderRightColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderLeft: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.left = `${x + this.offset}px`;
						break;
					case "south":
						this.triangleStyle = {
							...this.triangleStyle,
							top: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderBottomColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderTop: "none",
						};
						element.style.top = `${y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "west":
						this.triangleStyle = {
							...this.triangleStyle,
							right: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderLeftColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderRight: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.right = `${viewportMaxX - x + this.offset}px`;
						break;
				}
			}
		},
		async close() {
			if (!this.dialog) {
				return;
			}

			this.dialog = false;
			await this.$nextTick();
		},
		determineDirection(x, y) {
			let direction = "south";
			if (this.bounds.bottom - y < this.height) {
				direction = "north";
			}
			if (x - this.bounds.left < this.width / 2) {
				direction = "east";
			}
			if (this.bounds.right - x < this.width / 2) {
				direction = "west";
			}

			return direction;
		},
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	position: relative;
	display: flex;
}

.triangle {
	position: absolute;
	width: 0;
	height: 0;
	border: 8px solid transparent;
}
</style>
